import * as React from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { observer } from "mobx-react";
import { ColumnProps } from "antd/lib/table";
import { SortOrder } from "antd/lib/table/interface";

import { TableColumnHeader } from "Components/General/ColumnHeaderTooltip";
import { ModalConsumer } from "Components/General/Modal/ModalContext";
import { ReproServiceForm } from "./ReproServiceForm";
import { Button } from "Components/General/Button";
import { stringSort } from "utils/defaultSort";
import { Loader } from "Components/General/Loader";
import { Row, Popconfirm } from "antd";
import { API } from "General/API";
import { API_URL } from "General/APIEndpoints";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classnames from "classnames";
import { withRouter, WithRouterProps } from "Components/General/WithRouter";
import { Table } from "Components/General/Table";

interface TableSorter {
  columnKey: string;
  order: "ascend" | "descend";
}

export interface ReproServiceProps extends WithTranslation, WithRouterProps {
  // this flag changes the endpoint which is used to get project info wether a Template project is cloned
  // or a regular one
  // https://pmgredesign.atlassian.net/browse/DEV-6798
  cloneProjectTemplate?: boolean;
}

interface ReproServiceState {
  project: API_Project;
  providersList: API_PrintJobProvider[];
  projectProviders: API_PrintJobProvider[];
  loading: boolean;
  sortedInfo: TableSorter;
}

export const ReproService = withTranslation(I18N_NS.ADMIN)(
  withRouter(
    observer(
      class extends React.Component<ReproServiceProps, ReproServiceState> {
        private MODAL_PROPS = { width: `100%`, style: { maxWidth: 620 }, footer: null };

        constructor(props: ReproServiceProps) {
          super(props);
          this.state = {
            project: null,
            providersList: [],
            projectProviders: [],
            loading: true,
            sortedInfo: {
              columnKey: "selected",
              order: "ascend",
            },
          };
        }

        componentDidMount() {
          (async () => await this.handleFetch())();
        }

        get reproServiceColumns(): ColumnProps<API_PrintJobProvider>[] {
          const { t } = this.props;
          const { providersList } = this.state;

          return [
            {
              dataIndex: "id",
              title: ({ sortOrder }) => this.renderHeaderCell(sortOrder, "ID", "id"),
              sorter: (a, b) => stringSort(a, b, "id"),
            },
            {
              dataIndex: "name",
              title: ({ sortOrder }) => this.renderHeaderCell(sortOrder, "Name", "name"),
              sorter: (a, b) => stringSort(a, b, "name"),
            },
            {
              dataIndex: "display",
              title: ({ sortOrder }) => this.renderHeaderCell(sortOrder, "Alternative display", "display"),
              sorter: (a, b) => stringSort(a, b, "display"),
            },
            {
              dataIndex: "email",
              title: ({ sortOrder }) => this.renderHeaderCell(sortOrder, "Email", "email"),
              sorter: (a, b) => stringSort(a, b, "email"),
            },
            {
              dataIndex: "orderMode",
              title: t("Order mode"),
              sorter: (a, b) => stringSort(a, b, "orderMode"),
            },
            {
              dataIndex: "priority",
              title: ({ sortOrder }) => this.renderHeaderCell(sortOrder, "Priority", "priority"),
              sorter: (a, b) => stringSort(a, b, "priority"),
              defaultSortOrder: "ascend",
            },
            {
              dataIndex: "actions",
              title: t("Actions"),
              render: (text, record) =>
                record.id < 1 ? null : (
                  <>
                    <ModalConsumer>
                      {({ showModal }) => (
                        <span
                          style={{ margin: "0 10px", cursor: "pointer" }}
                          onClick={() =>
                            showModal(ReproServiceForm, {
                              modalProps: { ...this.MODAL_PROPS, title: t("Change repro service") },
                              componentProps: {
                                onEdit: this.handleEdit,
                                onCreate: this.handleCreate,
                                projectName: this.state.project.name,
                                initialData: record,
                                providers: providersList,
                              },
                            })
                          }
                        >
                          <FontAwesomeIcon fixedWidth={true} icon={["fas", "edit"]} />
                        </span>
                      )}
                    </ModalConsumer>
                    {this.state.projectProviders.length >= 1 ? (
                      <Popconfirm title="Sure to delete?" onConfirm={() => this.handleDelete(record)}>
                        <FontAwesomeIcon fixedWidth={true} icon={["fas", "trash"]} style={{ cursor: "pointer" }} />
                      </Popconfirm>
                    ) : null}
                  </>
                ),
            },
          ];
        }

        handleFetch = async () => {
          try {
            this.setState({ loading: true });
            const projectId = +this.props.params.id;
            let project;
            if (this.props.cloneProjectTemplate) {
              project = await API.getData(API_URL.ADMIN_TEMPLATES.getTemplate(projectId));
            } else {
              project = await API.getData(API_URL.ADMIN_PROJECTS.getProject(projectId));
            }
            const providersList: API_PrintJobProvider[] = await API.getData(API_URL.ADMIN_PROJECTS.getAllProviders_1());
            // tslint:disable-next-line:max-line-length
            const projectProviders: API_PrintJobProvider[] = await API.getData(
              API_URL.PROVIDER.getProjectProviders(projectId)
            );

            this.setState({ project, projectProviders, providersList });
          } catch (err) {
            throw err;
          } finally {
            this.setState({ loading: false });
          }
        };

        handleCreate = async (obj: API_PrintJobProvider) => {
          try {
            const projectId = this.props.params.id;
            await API.newData(API_URL.PROVIDER.assignReproService(projectId, obj.id), obj);
            // tslint:disable-next-line:max-line-length
            const projectProviders: API_PrintJobProvider[] = await API.getData(
              API_URL.PROVIDER.getProjectProviders(projectId)
            );

            this.setState({ projectProviders });
          } catch (error) {
            throw error;
          }
        };

        handleEdit = async (obj: API_PrintJobProvider) => {
          try {
            const projectId = this.props.params.id;
            const projectProviders = this.state.projectProviders.slice();
            const index = projectProviders.findIndex((item) => item.id === obj.id);
            if (index !== -1) {
              projectProviders[index] = obj;
            }
            this.setState({ projectProviders });
            await API.editData(API_URL.PROVIDER.updateReproService(projectId, obj.assignmentId), obj);
          } catch (error) {
            throw error;
          }
        };

        handleDelete = async (obj: API_PrintJobProvider) => {
          try {
            const projectId = this.props.params.id;
            const projectProviders = [...this.state.projectProviders].filter((provider) => provider.id !== obj.id);
            this.setState({ projectProviders });
            await API.deleteData(API_URL.PROVIDER.unassignReproService(projectId, obj.assignmentId), obj);
          } catch (error) {
            throw error;
          }
        };

        renderHeaderCell = (sortOrder: SortOrder = "ascend", title: string, columnIndex: string) => {
          const { t } = this.props;
          const { columnKey } = this.state.sortedInfo;

          return columnKey === columnIndex ? <TableColumnHeader title={t(title)} sortOrder={sortOrder} /> : t(title);
        };

        render() {
          if (this.state.loading) {
            return <Loader />;
          }

          const { projectProviders, providersList, loading } = this.state;
          const { t, cloneProjectTemplate } = this.props;

          return (
            <div className={classnames({ "g-card": !cloneProjectTemplate })}>
              <Row justify="space-between" className="mb-lg">
                <h1>
                  {t("Project configuration")}: <strong>{this.state.project?.name}</strong>
                </h1>
                <ModalConsumer>
                  {({ showModal }) => (
                    <div className="text-right">
                      <Button
                        type="primary"
                        faIcon={"plus"}
                        onClick={() =>
                          showModal(ReproServiceForm, {
                            modalProps: {
                              ...this.MODAL_PROPS,
                              title: (
                                <>
                                  {t("Add repro service to project")} <strong>{this.state.project?.name}</strong>
                                </>
                              ),
                            },
                            componentProps: {
                              onEdit: this.handleEdit,
                              onCreate: this.handleCreate,
                              providers: providersList,
                            },
                          })
                        }
                      >
                        {t("New service")}
                      </Button>
                    </div>
                  )}
                </ModalConsumer>
              </Row>
              <Table<API_PrintJobProvider>
                name="adminRepros"
                rowKey="id"
                dataSource={projectProviders}
                loading={loading}
                columns={this.reproServiceColumns}
                onChange={this.handleChange}
                pagination={{ hideOnSinglePage: true }}
              />
            </div>
          );
        }

        private handleChange = (pagination: {}, filters: {}, sorter: TableSorter) => {
          this.setState({
            sortedInfo: sorter,
          });
        };
      }
    )
  )
);
